import { useEffect, useCallback } from 'react';

export const useScrollSync = ({
  categorias,
  categoriaID,
  setCategoriaID,
  scrollingToSection,
  categoriasSinProductos,
  itemsRef,
  getID
}) => {
  const centerTab = useCallback((index, immediate = false) => {
    if (!itemsRef.current) return;
    
    const botones = itemsRef.current.querySelectorAll('button');
    if (!botones || index >= botones.length) return;
    
    const tab = botones[index];
    const container = itemsRef.current;
    
    if (container && tab) {
      const offset = tab.getBoundingClientRect().left - container.getBoundingClientRect().left;
      const scrollTo = offset - container.offsetWidth / 2 + tab.offsetWidth / 2;
      
      container.scrollTo({
        left: scrollTo,
        behavior: immediate ? 'auto' : 'smooth'
      });
    }
  }, [itemsRef]);

  useEffect(() => {
    const mainContainer = document.querySelector('[class*="productos_container"]');
    if (!mainContainer || !itemsRef.current) {
      return;
    }
    
    let lastSelectedIndex = -1;
    let isProcessing = false;
    
    const actualizarBotones = (indiceActivo) => {
      if (!itemsRef.current || isProcessing || indiceActivo === lastSelectedIndex) return;
      
      isProcessing = true;
      lastSelectedIndex = indiceActivo;
      
      const botones = itemsRef.current.querySelectorAll('button');
      
      for (let i = 0; i < botones.length; i++) {
        if (i === indiceActivo) {
          botones[i].style.backgroundColor = '#e9c05f';
          botones[i].style.color = '#25363e';
          botones[i].style.fontWeight = 'bold';
          
          centerTab(indiceActivo);
          
          const categoriasVisibles = categorias.filter(cat => !categoriasSinProductos.has(getID(cat)));
          if (categoriasVisibles && categoriasVisibles.length > indiceActivo) {
            const categoria = categoriasVisibles[indiceActivo];
            const id = categoria._id || categoria.id;
            
            if (id !== categoriaID) {
              setCategoriaID(id);
            }
          }
        } else {
          botones[i].style.backgroundColor = '';
          botones[i].style.color = '';
          botones[i].style.fontWeight = '';
        }
      }
      
      setTimeout(() => {
        isProcessing = false;
      }, 50);
    };
    
    const calculateVisibility = (element) => {
      if (!element) return 0;
      
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
      const elementHeight = rect.height;
      
      return visibleHeight > 0 ? visibleHeight / elementHeight : 0;
    };
    
    const getMostVisibleSection = () => {
      const secciones = Array.from(document.querySelectorAll('[class*="seccion_categoria"]'));
      let maxVisibility = 0;
      let mostVisibleIndex = -1;
      
      secciones.forEach((seccion, index) => {
        const visibility = calculateVisibility(seccion);
        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleIndex = index;
        }
      });
      
      return mostVisibleIndex;
    };
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollingToSection || isProcessing) return;
        
        requestAnimationFrame(() => {
          const mostVisibleIndex = getMostVisibleSection();
          if (mostVisibleIndex !== -1 && mostVisibleIndex !== lastSelectedIndex) {
            actualizarBotones(mostVisibleIndex);
          }
        });
      },
      {
        root: null,
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7]
      }
    );
    
    const categoryTabs = document.querySelectorAll('[class*="categoria_tab"]');
    categoryTabs.forEach(tab => {
      observer.observe(tab);
    });
    
    const handleScroll = () => {
      if (scrollingToSection || isProcessing) return;
      
      requestAnimationFrame(() => {
        const mostVisibleIndex = getMostVisibleSection();
        if (mostVisibleIndex !== -1 && mostVisibleIndex !== lastSelectedIndex) {
          actualizarBotones(mostVisibleIndex);
        }
      });
    };
    
    mainContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    const initializeActiveTab = () => {
      if (scrollingToSection) return;
      
      const mostVisibleIndex = getMostVisibleSection();
      if (mostVisibleIndex !== -1) {
        actualizarBotones(mostVisibleIndex);
      }
    };
    
    const timerID = setTimeout(initializeActiveTab, 100);
    
    return () => {
      observer.disconnect();
      clearTimeout(timerID);
      mainContainer.removeEventListener('scroll', handleScroll);
    };
  }, [categorias, categoriaID, scrollingToSection, categoriasSinProductos, centerTab, setCategoriaID, getID, itemsRef]);
};